import React, { useMemo, type ReactNode } from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { RecordProvider } from './Context';
import Rules from './Rules';
import { commonStyles } from './styles';
import Table from './Table';

export type RecordId = string | number;

export interface SupportRule {
  inputName: string;
  defaultValue?: unknown;
  [key: string]: unknown;
}

// `do` 用 method 語法（bivariant）讓呼叫端可定義較窄的 selected 型別
// （例如某些表格 ID 都是 string）而不需要 cast。
// 不加 `[key: string]: unknown` index sig，否則呼叫端的 strict interface（無 index sig）
// 會被 TS 視為不相容（"Index signature for type 'string' is missing"）。
export interface TableAction {
  name: string;
  icon?: ReactNode;
  do(selected: any): void;
  persist?: boolean;
  type?: string;
  danger?: boolean;
}

export interface TableService {
  // getRecords 為選填以放行 service 物件的鬆散 shape；呼叫端 service 通常用
  // `[key: string]: any` 兜底，不會強制宣告 getRecords 簽名。
  getRecords?: (payload: Record<string, unknown>) => Promise<{ data: unknown[]; has_more: boolean }>;
  getIds?: (payload: Record<string, unknown>) => Promise<{ data: RecordId[] }>;
  [key: string]: unknown;
}

// batchEditPanel 是 render-prop 物件而非 ReactNode；render 接到的 args
// 內含 selected/showPanel/onClose/onSubmit/query，呼叫端可只取其中一部分。
export interface BatchEditPanelRenderProp {
  render: (args: any) => ReactNode;
}

export interface SearchableConfig {
  match?: 'prefix' | 'contains';
  maxCount?: number;
  placeholder?: ReactNode;
  tooltipContent?: ReactNode;
  /** 可搜尋欄位的「已翻譯」標籤陣列（重用 models.* 標籤）；FilterComponent 會自動組成「可搜尋欄位：A、B、C」提示 */
  searchFields?: string[];
}

export interface TableIndexProps {
  supportRules?: SupportRule[];
  columns: unknown[];
  queries?: Record<string, unknown>;
  searchable?: boolean | SearchableConfig;
  actions?: TableAction[];
  defaultQuery?: Record<string, unknown>;
  columnsEditable?: boolean;
  tableId: string;
  lastModified?: number;
  setLastModified?: (value: number) => void;
  showPanel?: boolean;
  onClosePanel?: () => void;
  /** Only service is allowed */
  service: TableService;
  /** 批次編輯面板配置 */
  batchEditPanel?: BatchEditPanelRenderProp;
  /** 當篩選條件變更時的回調 */
  onQueryChange?: (query: Record<string, unknown>) => void;
  /** 篩選器說明的自訂範例 */
  filterHelpExample?: string;
  /** 篩選器說明文件連結 */
  filterDocsUrl?: string;
  /** 搜尋提示的自訂內容 */
  searchTooltipContent?: ReactNode;
  /** 標準化新增按鈕跳轉路徑 */
  newPath?: string;
  /** 標準化新增按鈕點擊事件 */
  onCreate?: () => void;
  /** 標準化匯出按鈕點擊事件 (Batch Action) */
  onExport?: (selectedIds: RecordId[]) => void;
  /** 標準化匯入按鈕點擊事件 (Global Action) */
  onImportClick?: () => void;
  /** E2E 測試用的 data-testid 前綴 (e.g. 'product') */
  testIdPrefix?: string;
  /** 篩選器與表格之間的自訂內容 */
  afterFilter?: ReactNode;
  /** 是否啟用展開列 */
  expandableRows?: boolean;
  /** 展開列的渲染元件 */
  expandableRowsComponent?: React.ComponentType<any>;
}

const TableIndex: React.FC<TableIndexProps> = ({
  supportRules = [],
  columns,
  queries,
  searchable,
  actions,
  defaultQuery,
  columnsEditable,
  tableId,
  lastModified,
  setLastModified,
  showPanel,
  onClosePanel,
  service,
  batchEditPanel,
  onQueryChange,
  filterHelpExample: filterHelpExampleProp,
  filterDocsUrl,
  searchTooltipContent,
  newPath,
  onCreate,
  onExport,
  onImportClick,
  testIdPrefix,
  afterFilter,
  expandableRows,
  expandableRowsComponent,
}) => {
  const { t } = useTranslation();

  const filterHelpExample = filterHelpExampleProp || t('common.table.filters.filter_help.example');

  // onExport is now treated as a Batch Action, so it doesn't affect Toolbar visibility
  const hasFilterFeatures = searchable || supportRules.length > 0 || newPath || onCreate || onImportClick;

  const tableActions = useMemo<TableAction[]>(() => {
    const baseActions = actions || [];
    if (onExport) {
      return [
        ...baseActions,
        {
          name: 'common.export',
          icon: <DownloadOutlined />,
          do: (selected) => {
            // selected is a Set or Array of IDs depending on implementation.
            // SubHeader calls it with `selected` (Set).
            // Convert Set to Array for easier usage.
            const selectedIds = Array.from(selected) as RecordId[];
            onExport(selectedIds);
          },
          persist: false, // Only show when items are selected
          type: 'default',
        },
      ];
    }
    return baseActions;
  }, [actions, onExport]);

  return (
    <RecordProvider
      supportRules={supportRules}
      tableId={tableId}
      lastModified={lastModified}
      queries={queries}
      defaultQuery={defaultQuery}
      service={service}
      onQueryChange={onQueryChange}
    >
      <div style={{ width: '100%' }}>
        {hasFilterFeatures && (
          <div style={commonStyles.filterPanel}>
            <Rules
              supportRules={supportRules}
              tableId={tableId}
              searchable={searchable}
              lastModified={lastModified}
              {...(supportRules.length > 0 && {
                filterHelpExample: filterHelpExample,
                filterDocsUrl: filterDocsUrl,
              })}
              searchTooltipContent={searchTooltipContent}
              newPath={newPath}
              onCreate={onCreate}
              onImportClick={onImportClick}
              testIdPrefix={testIdPrefix}
            />
          </div>
        )}
        {afterFilter}
        <div>
          <Table
            columnsEditable={columnsEditable}
            tableId={tableId}
            columns={columns as any}
            queries={queries}
            actions={tableActions}
            setLastModified={setLastModified}
            showPanel={showPanel}
            onClosePanel={onClosePanel}
            batchEditPanel={batchEditPanel}
            testIdPrefix={testIdPrefix}
            expandableRows={expandableRows}
            expandableRowsComponent={expandableRowsComponent}
          />
        </div>
      </div>
    </RecordProvider>
  );
};

export default TableIndex;
